/**
 * SimHash: 64-bit locality-sensitive fingerprint for fuzzy book matching.
 *
 * Used to determine if two uploaded files are the "same book" (tolerating
 * minor formatting/whitespace differences across editions).
 *
 * Algorithm:
 *   1. Tokenize text (English: split on whitespace; CJK: split by character)
 *   2. Extract all 3-grams (sliding window of 3 consecutive tokens)
 *   3. Hash each 3-gram to a 64-bit value (FNV-1a via BigInt)
 *   4. Weighted voting across all 64 bit positions
 *   5. Produce a 64-bit fingerprint
 *
 * Matching thresholds:
 *   Hamming distance ≤ 3   → same edition
 *   Hamming distance 4-10  → same book, different edition
 *   Hamming distance > 10  → different book
 *
 * Stored as TEXT hex string (not INTEGER) to avoid JS Number precision loss.
 */

// FNV-1a 64-bit hash constants
const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

/**
 * FNV-1a 64-bit hash of a string.
 * @param {string} str
 * @returns {bigint} 64-bit hash
 */
function fnv1a64(str) {
  let hash = FNV_OFFSET;
  for (let i = 0; i < str.length; i++) {
    hash ^= BigInt(str.charCodeAt(i));
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash;
}

/**
 * Tokenize text for SimHash.
 * English/Latin: split on whitespace, lowercase.
 * CJK: split by individual character.
 * Mixed: combination of both.
 * @param {string} text
 * @returns {string[]}
 */
function tokenize(text) {
  const tokens = [];
  // Match either runs of word characters (Latin/digits) or individual CJK characters
  const re = /[\w]+|[\u4E00-\u9FFF\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    tokens.push(m[0].toLowerCase());
  }
  return tokens;
}

/**
 * Extract n-grams from token array.
 * @param {string[]} tokens
 * @param {number} n - gram size (default 3)
 * @returns {string[]}
 */
function ngrams(tokens, n = 3) {
  if (tokens.length < n) return [tokens.join(' ')];
  const result = [];
  for (let i = 0; i <= tokens.length - n; i++) {
    result.push(tokens.slice(i, i + n).join(' '));
  }
  return result;
}

/**
 * Compute 64-bit SimHash from text.
 * @param {string} text - First ~10,000 characters of the book body
 * @returns {string} Hex string representation of the 64-bit SimHash
 */
export function computeSimHash(text) {
  if (!text || typeof text !== 'string' || text.trim().length === 0) return null;
  const tokens = tokenize(text);
  if (tokens.length === 0) return null;
  const grams = ngrams(tokens, 3);

  // Weighted voting: for each bit position, sum +1 (bit=1) or -1 (bit=0)
  const votes = new Int32Array(64);

  for (const gram of grams) {
    const hash = fnv1a64(gram);
    for (let i = 0; i < 64; i++) {
      if ((hash >> BigInt(i)) & 1n) {
        votes[i]++;
      } else {
        votes[i]--;
      }
    }
  }

  // Build fingerprint: bit=1 if votes positive, bit=0 otherwise
  let fingerprint = 0n;
  for (let i = 0; i < 64; i++) {
    if (votes[i] > 0) {
      fingerprint |= (1n << BigInt(i));
    }
  }

  return fingerprint.toString(16).padStart(16, '0');
}

/**
 * Compute Hamming distance between two SimHash hex strings.
 * @param {string} a - Hex string
 * @param {string} b - Hex string
 * @returns {number} Number of differing bits (0-64)
 */
export function hammingDistance(a, b) {
  if (!a || !b) return 64; // max distance for missing hashes
  let xor = BigInt(`0x${a}`) ^ BigInt(`0x${b}`);
  let count = 0;
  while (xor > 0n) {
    count += Number(xor & 1n);
    xor >>= 1n;
  }
  return count;
}

/**
 * Extract the first ~10,000 characters of book body text for SimHash computation.
 * Skips front matter (TOC, preface) by looking for the first substantial chapter.
 * @param {string[]} chapterTexts - Array of plain text per chapter (from extractVisibleText)
 * @param {number} targetLength - Target text length (default 10000)
 * @returns {string}
 */
export function extractSimHashInput(chapterTexts, targetLength = 10000) {
  let result = '';
  // Skip very short chapters (likely front matter: title page, copyright, TOC)
  const FRONT_MATTER_THRESHOLD = 200;
  let started = false;

  for (const text of chapterTexts) {
    if (!started && text.length < FRONT_MATTER_THRESHOLD) continue;
    started = true;
    result += text + ' ';
    if (result.length >= targetLength) break;
  }

  // Fallback: if all chapters are short, use everything
  if (!result) {
    result = chapterTexts.join(' ');
  }

  return result.slice(0, targetLength);
}
